import React, { useEffect, useRef, useState } from 'react';
import {
  Container,
  Stack,
  Backdrop,
  CircularProgress,
  Typography,
  TextField,
  Button
} from '@mui/material';
import { Form, FormikProvider, useFormik } from 'formik';
import { LoadingButton } from '@mui/lab';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ReactPannellum from 'react-pannellum';
import UploadPreview from '../components/uploads/UploadPreview';
import Page from '../components/Page';
import { getWork, newWork, updateWork } from '../utils/requests/workRequest';

export function Add3DProjects() {
  const [isLoading, setIsLoading] = useState(false);
  const [file, setFile] = useState('');
  const [fileUrl, setFileUrl] = useState('');

  const navigate = useNavigate();

  const [titleError, setTitleError] = useState('');
  const [companyError, setCompanyError] = useState('');
  const [widthError, setWidthError] = useState('');
  const [heightError, setHeightError] = useState('');

  const { id } = useParams();

  /**
   * @type {React.MutableRefObject<HTMLInputElement>}
   */
  const titleInput = useRef();

  /**
   * @type {React.MutableRefObject<HTMLInputElement>}
   */
  const companyInput = useRef();

  /**
   * @type {React.MutableRefObject<HTMLInputElement>}
   */
  const heightInput = useRef();

  /**
   * @type {React.MutableRefObject<HTMLInputElement>}
   */
  const widthInput = useRef();

  const formik = useFormik({
    initialValues: {},
    onSubmit: () => {
      const title = titleInput.current?.value;
      const company = companyInput.current?.value;
      const width = widthInput.current.value ?? 0;
      const height = heightInput.current.value ?? 0;

      setTitleError(false);
      setCompanyError(false);

      setIsLoading(true);

      let work;
      if (id) {
        work = updateWork(id, title, company, file, 3, undefined, width, height);
      } else {
        work = newWork(title, company, file, 3, undefined, width, height);
      }

      work
        .then((response) => {
          toast.success(response.data.message);
          navigate('../../');
        })
        .catch((error) => {
          if (error.response) {
            const { data } = error.response;
            if (data.data.title) {
              setTitleError(data.data.title[0]);
            }
            if (data.data.width) {
              setWidthError(data.data.width[0]);
            }
            if (data.data.height) {
              setHeightError(data.data.height[0]);
            }
            if (data.data.details) {
              setCompanyError(data.data.details[0]);
            }
            if (data.data.file) {
              toast.error(data.data.file[0]);
            }
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  });

  useEffect(() => {
    if (!id) return;
    getWork(id).then((work) => {
      titleInput.current.value = work.title;
      companyInput.current.value = work.details;
      widthInput.current.value = work.gallery.size.width;
      heightInput.current.value = work.gallery.size.height;
      setFileUrl(`${process.env.REACT_APP_API}works/${work.id}/file`);
    });
  }, [id]);

  /**
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  function onFileChange(e) {
    const file = e.target.files[0];
    setFile(file);
    setFileUrl(URL.createObjectURL(file));
  }

  const config = {
    autoLoad: true
  };

  return (
    <Page>
      <Container>
        <Stack spacing={2}>
          <Backdrop
            sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={isLoading}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        </Stack>
        <Typography variant="h4" sx={{ mb: 5 }}>
          Add new 3D project
        </Typography>
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate>
            <Stack style={{ width: '100%' }} spacing={2}>
              <Stack spacing={2} direction="row">
                <Stack sx={{ width: '100%' }}>
                  <span>File</span>
                  <UploadPreview
                    onChange={(e) => onFileChange(e)}
                    currentPhoto={fileUrl}
                    style={{ width: '100%' }}
                    accept="images/*"
                  />
                </Stack>
              </Stack>
              {fileUrl !== '' ? (
                <>
                  <ReactPannellum
                    sceneId="firstScene"
                    id="d_view"
                    config={config}
                    imageSource={fileUrl}
                    style={{ width: '50%', height: '150px' }}
                  />
                </>
              ) : (
                <></>
              )}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  error={titleError}
                  disabled={isLoading}
                  helperText={titleError}
                  fullWidth
                  type="text"
                  label="Project title"
                  inputRef={titleInput}
                />
                <TextField
                  error={companyError}
                  helperText={companyError}
                  fullWidth
                  disabled={isLoading}
                  type="text"
                  label="Company"
                  inputRef={companyInput}
                />
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  error={widthError}
                  disabled={isLoading}
                  helperText={widthError}
                  fullWidth
                  type="number"
                  label="Width in home"
                  defaultValue={0}
                  inputRef={widthInput}
                />
                <TextField
                  error={heightError}
                  helperText={heightError}
                  fullWidth
                  disabled={isLoading}
                  type="number"
                  label="Height in home"
                  defaultValue={0}
                  inputRef={heightInput}
                />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <LoadingButton loading={isLoading} variant="contained" type="submit">
                  Save
                </LoadingButton>
                <Button disabled={isLoading} variant="text" onClick={() => navigate('../../')}>
                  Cancel
                </Button>
              </Stack>
            </Stack>
          </Form>
        </FormikProvider>
      </Container>
    </Page>
  );
}
